/**
 * mobile_strategy §2.2 — the friendly fallback for a desktop-only route
 * opened on a phone.
 *
 * "Do NOT down-port desktop tools to phones — gating them with a clear
 * message is the feature, not a compromise." So this is not an error page: it
 * names the constraint in one line and then does the useful thing, which is
 * offering the parts of the same workspace that *do* work on a phone.
 */
import type { ReactElement } from "react";
import { Button, Paper, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { PageHeading } from "@aeriosiq/ui";
import { useActiveWorkspace } from "../workspaces/useWorkspaces.js";
import { isMobileCapable } from "../workspaces/routeDevice.js";
import { HOME_PATH } from "../workspaces/resolve.js";
import { MonitorIcon } from "@aeriosiq/ui/icons";

export function DesktopOnlyPage(): ReactElement {
  const { workspace, items } = useActiveWorkspace();
  const { pathname } = useLocation();

  const alternatives = items.filter(
    (i) =>
      i.enabled !== false &&
      i.to !== undefined &&
      i.to !== pathname &&
      isMobileCapable(i.to),
  );

  return (
    <Stack spacing={2}>
      <PageHeading>This tool needs a desktop</PageHeading>
      <Paper
        variant="outlined"
        role="region"
        aria-label="Desktop only"
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          textAlign: "center",
        }}
      >
        <MonitorIcon
          sx={{ fontSize: 44, color: "text.secondary" }}
          aria-hidden
        />
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", maxWidth: 420 }}
        >
          This page needs a bigger screen and a pointer — open it on a laptop or
          desktop. Nothing is lost; your work is on the server.
        </Typography>
        {alternatives.length > 0 ? (
          <Stack spacing={1} alignItems="center" sx={{ pt: 1 }}>
            <Typography variant="caption" sx={{ color: "text.tertiary" }}>
              {workspace
                ? `Available here on your phone`
                : "Available on your phone"}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              justifyContent="center"
              useFlexGap
            >
              {alternatives.map((item) => (
                <Button
                  key={item.label}
                  component={RouterLink}
                  to={item.to ?? HOME_PATH}
                  size="small"
                  variant="outlined"
                >
                  {item.label}
                </Button>
              ))}
            </Stack>
          </Stack>
        ) : (
          <Button component={RouterLink} to={HOME_PATH} variant="outlined">
            Back to Home
          </Button>
        )}
      </Paper>
    </Stack>
  );
}

/**
 * Route-level capability gate. Sits once at the protected shell and consults
 * the `routeDevice` table, so a new desktop-only page is one table entry — no
 * per-route wrapper to forget.
 */
export function DeviceGate({
  isMobile,
  children,
}: {
  isMobile: boolean;
  children: ReactElement;
}): ReactElement {
  const { pathname } = useLocation();
  if (isMobile && !isMobileCapable(pathname)) return <DesktopOnlyPage />;
  return children;
}
